import { useCallback, useRef } from 'react';

/**
 * Pause while scrubbing, resume on release, but only if it was playing when
 * the drag began.
 *
 * Scrubbing a playing video is laggy because playback keeps advancing the
 * clock while the drag issues seeks, so the two fight: every seek lands, then
 * playback immediately moves past it, and the next seek starts from somewhere
 * the coach did not choose. Pausing for the duration of the drag removes the
 * competing writer. It is what YouTube does, and the reason it feels smooth
 * there and not here.
 *
 * The rule that matters is the conditional resume. A video the coach had
 * already paused must still be paused when they let go — scrubbing is how you
 * look for a frame, and starting playback because you looked is the wrong
 * answer.
 *
 * `begin` and `end` are idempotent. Pointer streams do not always arrive in
 * the order you would like: `pointerup` and `pointercancel` can both fire, a
 * `change` can land after a `pointerup`, and a drag can be interrupted by the
 * element unmounting. Calling `end` twice must not start playback twice, and
 * calling `begin` twice must not overwrite the captured state with "paused"
 * once the first call has already paused it. That second one is the subtle
 * failure: without the guard, a double `begin` would record the video as
 * having been paused and never resume it.
 */
export function useScrubPause(getVideo: () => HTMLVideoElement | null) {
  /** Whether a scrub is currently open. Guards both calls against repeats. */
  const activeRef = useRef(false);
  /** Whether playback was running when this scrub began. */
  const resumeRef = useRef(false);

  const begin = useCallback(() => {
    if (activeRef.current) return;
    const video = getVideo();
    if (!video) return;
    activeRef.current = true;
    /* `ended` counts as not playing: resuming an ended video would restart it
       from wherever the scrub finished, which is not what releasing the
       handle asked for. */
    resumeRef.current = !video.paused && !video.ended;
    if (resumeRef.current) video.pause();
  }, [getVideo]);

  const end = useCallback(() => {
    if (!activeRef.current) return;
    activeRef.current = false;
    if (!resumeRef.current) return;
    resumeRef.current = false;
    const video = getVideo();
    if (!video) return;
    /* Swallowed deliberately. A rejected resume leaves the video paused, which
       is the same place the coach already was; surfacing it would be noise on
       a gesture they have finished making. */
    video.play().catch(() => { /* stays paused */ });
  }, [getVideo]);

  return { begin, end };
}
